import React, { useMemo } from 'react';
import { Modal, Button, Empty, Tag, Typography } from 'antd';
import { CheckCircleTwoTone, CloseCircleTwoTone } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from 'shared/hooks';
import { closeModal } from 'services/store/reducers/modalReducer';
import { TableLECompletionReportVrUser } from 'shared/types';
import { compareLeKeys } from 'utils/columns';

type Props = {
  user: TableLECompletionReportVrUser;
};

type StationStatus = { index: number; done: boolean };

type LeRow = {
  leKey: string;
  leName: string;
  stationCount: number;
  stations: StationStatus[];
  doneCount: number;
};

const stationsToArray = (
  stations: Record<string, boolean> | boolean[] | undefined,
  stationCount: number,
): StationStatus[] => {
  const result: StationStatus[] = [];
  // Station 0 is excluded — backend treats it as the lobby/intro and never
  // counts it toward completion.
  for (let i = 1; i < stationCount; i++) {
    let done = false;
    if (Array.isArray(stations)) {
      done = !!stations[i];
    } else if (stations && typeof stations === 'object') {
      done = !!stations[String(i)];
    }
    result.push({ index: i, done });
  }
  return result;
};

const StationsCompletionModal: React.FC<Props> = ({ user }): JSX.Element => {
  const dispatch = useAppDispatch();
  const isModalOpened = useAppSelector((state): boolean => state.modal.isModalOpened);

  const handleCancel = (): void => {
    dispatch(closeModal());
  };

  const rows = useMemo<LeRow[]>(() => {
    if (!user.leData) return [];
    return Object.entries(user.leData)
      .map(([leKey, le]) => {
        const stations = stationsToArray(le.stations, le.stationCount);
        const doneCount = stations.filter((s) => s.done).length;
        return {
          leKey,
          leName: le.leName ?? leKey,
          stationCount: le.stationCount,
          stations,
          doneCount,
        };
      })
      .filter((row) => row.stations.length > 0)
      .sort((a, b) => compareLeKeys(user.leData)(a.leKey, b.leKey));
  }, [user.leData]);

  const userLabel = [user.firstname, user.lastname].filter(Boolean).join(' ') || user.email;

  return (
    <Modal
      open={isModalOpened}
      title={`Station completion — ${userLabel}`}
      onCancel={handleCancel}
      footer={[
        <Button key='close' type='primary' onClick={handleCancel}>
          Close
        </Button>,
      ]}
      width={'820px'}
    >
      {rows.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description='No station data available for this user'
        />
      ) : (
        <div>
          {rows.map((row) => {
            const totalReal = row.stations.length;
            return (
              <div key={row.leKey} style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 8 }}>
                  <Typography.Text strong>
                    {row.leName}
                  </Typography.Text>
                  <Tag color={row.doneCount === totalReal ? 'green' : row.doneCount > 0 ? 'blue' : 'default'}>
                    {row.doneCount} / {totalReal} stations completed
                  </Tag>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {row.stations.map((station) => (
                    <div
                      key={station.index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '4px 10px',
                        border: '1px solid #f0f0f0',
                        borderRadius: 4,
                        background: station.done ? '#f6ffed' : '#fff1f0',
                      }}
                    >
                      {station.done ? (
                        <CheckCircleTwoTone twoToneColor='#52c41a' />
                      ) : (
                        <CloseCircleTwoTone twoToneColor='#ff4d4f' />
                      )}
                      <span>Station {station.index}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
};

export default StationsCompletionModal;
